import React, { useState, useEffect } from 'react';
import {
  Typography,
  ButtonPro,
  InputPro,
  TextAreaProControlled,
  Select
} from 'components';
import { Formik, Form } from 'formik';
import { StandaloneDatePicker } from '../../atoms/StandaloneDatePicker';
import './EditProjectModal.scss';
import { useAppDispatch, useAppSelector } from 'store';
import { updateProject } from 'store/MasterData';

interface Project {
  id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string | null;
  isActive: boolean;
  projectManager: string;
  projectManagerId: string;
  technicalLead: string;
  technicalLeadId: string;
}

interface EditProjectModalProps {
  isOpen: boolean;
  project: Project | null;
  onClose: () => void;
  onSave: (project: Project) => void;
}

export const EditProjectModal: React.FC<EditProjectModalProps> = ({
  isOpen,
  project,
  onClose,
  onSave
}) => {
  const dispatch = useAppDispatch();
  const [hasEndDate, setHasEndDate] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { employees } = useAppSelector((state) => state.masterData);

  const [formData, setFormData] = useState<Project>({
    id: '',
    name: '',
    description: '',
    startDate: '',
    endDate: null,
    isActive: true,
    projectManager: '',
    projectManagerId: '',
    technicalLead: '',
    technicalLeadId: ''
  });

  useEffect(() => {
    if (project) {
      setFormData({
        ...project,
        startDate: project.startDate.split('T')[0], // Format for date input
        endDate: project.endDate ? project.endDate.split('T')[0] : null
      });
      setHasEndDate(!!project.endDate); // Set checkbox state based on existing end date
    }
  }, [project]);

  const handleInputChange = (
    field: keyof Project,
    value: string | boolean | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleEndDateToggle = (checked: boolean) => {
    setHasEndDate(checked);
    if (!checked) {
      setFormData((prev) => ({
        ...prev,
        endDate: null
      }));
      // Clear end date error if exists
      if (errors.endDate) {
        setErrors((prev) => ({
          ...prev,
          endDate: ''
        }));
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Project name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Project description is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    // Only validate end date if user chose to set it
    if (hasEndDate) {
      if (!formData.endDate) {
        newErrors.endDate = 'End date is required when specified';
      } else if (
        formData.startDate &&
        formData.endDate &&
        new Date(formData.startDate) >= new Date(formData.endDate)
      ) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      dispatch(
        updateProject({
          id: formData.id,
          data: {
            name: formData.name,
            description: formData.description,
            startDate: formData.startDate + 'T00:00:00',
            endDate:
              hasEndDate && formData.endDate
                ? formData.endDate + 'T00:00:00'
                : null,
            isActive: formData.isActive,
            projectManagerId: formData.projectManagerId,
            technicalLeadId: formData.technicalLeadId
          }
        })
      );
      onClose();
    }
  };

  const handleClose = () => {
    setFormData({
      id: '',
      name: '',
      description: '',
      startDate: '',
      endDate: null,
      isActive: true,
      projectManager: '',
      projectManagerId: '',
      technicalLead: '',
      technicalLeadId: ''
    });
    setHasEndDate(false);
    setErrors({});
    onClose();
  };

  if (!isOpen || !project) return null;

  return (
    <div className="modal-overlay">
      <div className="edit-project-modal">
        <div className="modal-header">
          <Typography
            text="Edit Project"
            type="heading3"
            weight="bold"
            className="modal-title"
          />
          <ButtonPro
            label="×"
            type="icon"
            size="small"
            onClick={handleClose}
            buttonClassNames="close-button"
          />
        </div>

        <div className="modal-content">
          <div className="form-grid">
            <div className="form-group">
              <InputPro
                name="projectName"
                id="projectName"
                label="Project Name"
                required={true}
                value={formData.name}
                onChange={(value) => handleInputChange('name', value)}
                inputClass={errors.name ? 'error' : ''}
                placeholder="Enter project name"
              />
              {errors.name && (
                <span className="error-message">{errors.name}</span>
              )}
            </div>

            <div className="form-group full-width">
              <TextAreaProControlled
                label="Description"
                name="projectDescription"
                id="projectDescription"
                required={true}
                value={formData.description}
                onChange={(value) => handleInputChange('description', value)}
                inputClass={errors.description ? 'error' : ''}
                placeholder="Enter project description"
              />
              {errors.description && (
                <span className="error-message">{errors.description}</span>
              )}
            </div>

            <div className="form-group">
              <StandaloneDatePicker
                name="startDate"
                id="startDate"
                label="Start Date"
                value={formData.startDate}
                onChange={(value) => handleInputChange('startDate', value)}
                required={true}
                hasError={!!errors.startDate}
              />
              {errors.startDate && (
                <span className="error-message">{errors.startDate}</span>
              )}
            </div>

            <div className="form-group">
              <div className="checkbox-group" style={{ marginBottom: '8px' }}>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={hasEndDate}
                    onChange={(e) => handleEndDateToggle(e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  Set End Date
                </label>
              </div>

              {hasEndDate && (
                <>
                  <StandaloneDatePicker
                    name="endDate"
                    id="endDate"
                    label="End Date"
                    value={formData.endDate || ''}
                    onChange={(value) => handleInputChange('endDate', value)}
                    required={true}
                    hasError={!!errors.endDate}
                    minDate={formData.startDate} // End date should be after start date
                  />
                  {errors.endDate && (
                    <span className="error-message">{errors.endDate}</span>
                  )}
                </>
              )}

              {!hasEndDate && (
                <div className="no-end-date-info">
                  <Typography
                    text="No end date specified (ongoing project)"
                    type="small_regular"
                    className="text-muted"
                  />
                </div>
              )}
            </div>

            <div className="form-group">
              <Formik
                initialValues={{ projectManagerId: formData.projectManagerId }}
                enableReinitialize={true}
                onSubmit={() => {}}
              >
                <Form>
                  <Select
                    name="projectManagerId"
                    label="Project Manager"
                    options={[
                      { value: '', label: 'Select Project Manager' },
                      ...employees.map((emp) => ({
                        value: emp.employeeId,
                        label: emp.employeeName
                      }))
                    ]}
                    onChange={(option) => {
                      const value = option?.value || '';
                      const selectedEmployee = employees.find(
                        (emp) => emp.employeeId === value
                      );
                      handleInputChange('projectManagerId', value);
                      handleInputChange(
                        'projectManager',
                        selectedEmployee?.employeeName || ''
                      );
                    }}
                  />
                </Form>
              </Formik>
            </div>

            <div className="form-group">
              <Formik
                initialValues={{ technicalLeadId: formData.technicalLeadId }}
                enableReinitialize={true}
                onSubmit={() => {}}
              >
                <Form>
                  <Select
                    name="technicalLeadId"
                    label="Technical Lead"
                    options={[
                      { value: '', label: 'Select Technical Lead' },
                      ...employees.map((emp) => ({
                        value: emp.employeeId,
                        label: emp.employeeName
                      }))
                    ]}
                    onChange={(option) => {
                      const value = option?.value || '';
                      const selectedEmployee = employees.find(
                        (emp) => emp.employeeId === value
                      );
                      handleInputChange('technicalLeadId', value);
                      handleInputChange(
                        'technicalLead',
                        selectedEmployee?.employeeName || ''
                      );
                    }}
                  />
                </Form>
              </Formik>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    handleInputChange('isActive', e.target.checked)
                  }
                />
                <span className="checkmark"></span>
                Active Project
              </label>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <ButtonPro
            label="Cancel"
            type="white"
            size="base"
            onClick={handleClose}
          />
          <ButtonPro
            label="Save Changes"
            type="primary"
            size="base"
            onClick={handleSave}
          />
        </div>
      </div>
    </div>
  );
};
